"use client"

import {
  useCallback,
} from "react"
import { createPortal } from "react-dom"
import { Check, Plus, ChevronDown, X, GripVertical } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { cn } from "@/lib/utils"
import { usePointerListReorder } from "@/hooks/use-pointer-list-reorder"

const PHONE_TYPE_OPTIONS = [
  { value: "", label: "-" },
  { value: "main", label: "Main" },
  { value: "mobile", label: "Mobile" },
  { value: "cell", label: "Cell" },
  { value: "home", label: "Home" },
  { value: "work", label: "Work" },
  { value: "fax", label: "Fax" },
] as const

/** US state abbreviations and labels for address state dropdown. */
export const STATE_OPTIONS = [
  { value: "", label: "State" },
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" },
  { value: "DC", label: "District of Columbia" },
] as const

export type EmailEntry = { id: string; email: string }
export type PhoneEntry = { id: string; phone: string; phoneType: string; extension?: string }
export type AddressEntry = {
  id: string
  address?: string
  address2?: string
  city?: string
  state?: string
  zip?: string
}

/** Format digits as (XXX) XXX-XXXX. Non-digits are stripped from input. */
function formatPhoneDisplay(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 10)
  if (digits.length <= 3) return digits ? `(${digits}` : ""
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

/** Strip to digits only for storage/value (always 10 US digits, no +1). */
function phoneToDigits(value: string): string {
  return value.replace(/\D/g, "").slice(0, 10)
}

/** Normalize E.164 (+1...) or raw digits to 10 US digits for display/edit. */
function phoneToUs10(phone: string | undefined): string {
  if (!phone) return ""
  const digits = phone.replace(/\D/g, "")
  if (digits.length >= 11 && digits.startsWith("1")) return digits.slice(1, 11)
  return digits.slice(0, 10)
}

/** Parse combined "number x ext" input into main digits and extension digits. */
function parseCombinedPhoneExt(value: string): { phone: string; extension: string } {
  const match = value.match(/^(.*?)\s*x\s*(\d*)$/i)
  if (match) {
    const main = phoneToDigits(match[1])
    const ext = (match[2] ?? "").replace(/\D/g, "")
    return { phone: main, extension: ext }
  }
  return { phone: phoneToDigits(value), extension: "" }
}

/** Format E.164 or raw digits to (XXX) XXX-XXXX for display (no +1). */
function parsePhoneForInput(phone: string | undefined): string {
  return formatPhoneDisplay(phoneToUs10(phone))
}

/** Format E.164 phone for view display: (XXX) XXX-XXXX without +1. */
export function formatPhoneForDisplay(phone: string | undefined): string {
  return parsePhoneForInput(phone)
}

/** Normalize to 10 US digits for storage. */
export { phoneToDigits }

/** Normalize E.164 (+1...) or raw digits to 10 US digits. */
export { phoneToUs10 }

/** Format raw or E.164 to (XXX) XXX-XXXX for input display (handles partial). */
export { parsePhoneForInput }

function makeId(): string {
  return Math.random().toString(36).slice(2, 11)
}

export type ContactEditFormVariant = "page" | "modal"

function contactEditFormStyles(variant: ContactEditFormVariant) {
  const modal = variant === "modal"
  return {
    root: modal ? "grid grid-cols-2 items-stretch gap-4" : "space-y-4",
    column: modal ? "min-w-0 space-y-3" : "contents",
    columnDivider: modal
      ? "min-h-full min-w-0 space-y-3 border-l border-gray-300 pl-4 dark:border-gray-600"
      : "contents",
    sectionDivider: modal
      ? "border-t border-gray-300 pt-3 dark:border-gray-600"
      : "",
    nameSection: modal ? "" : "mb-4",
    input: cn(
      "rounded-sm border-gray-400 bg-white px-2 text-sm text-black dark:border-gray-500 dark:bg-white dark:text-black",
      modal ? "h-8 py-1" : "h-7 py-0.5 dark:bg-card"
    ),
    addBtn: cn(
      "shrink-0 gap-1 border border-slate-600 bg-gradient-to-b from-slate-100 to-sky-100 px-2 text-xs text-black shadow-[0_2px_0_0_rgb(0_0_0/0.08)] dark:border-slate-400 dark:from-slate-600 dark:to-sky-700 dark:text-black dark:shadow-[0_2px_0_0_rgb(0_0_0/0.2)]",
      modal ? "h-8" : "h-7"
    ),
    emailMaxW: modal ? "max-w-none w-full" : "max-w-[16.8rem]",
    addressMaxW: modal ? "max-w-none w-full" : "max-w-[16.8rem]",
    stateBtn: cn(
      "min-w-0 justify-between rounded-sm border-gray-400 font-normal dark:border-gray-500",
      modal ? "h-8" : "h-7"
    ),
  }
}

type PhoneRowContentProps = {
  p: PhoneEntry
  updatePhone: (id: string, phone: string, phoneType: string, extension?: string) => void
  removePhone: (id: string) => void
  handleCombinedPhoneExtChange: (id: string, rawValue: string) => void
  handlePhoneInputChange: (id: string, rawValue: string) => void
  inputClass: string
  stateBtnClass: string
}

/** Phone inputs + extension row (no drag handle). Shared by list row and floating preview. */
function PhoneRowContent({
  p,
  updatePhone,
  removePhone,
  handleCombinedPhoneExtChange,
  handlePhoneInputChange,
  inputClass,
  stateBtnClass,
}: PhoneRowContentProps) {
  const hasExtension = p.extension !== undefined
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        {hasExtension ? (
          <div className="grid w-max max-w-full grid-cols-[14rem_auto] items-center gap-x-1 gap-y-0.5">
            <Input
              type="tel"
              className={cn("col-start-1 row-start-1 min-w-0 w-full font-mono", inputClass)}
              value={
                phoneToDigits(p.phone).length >= 10
                  ? `${parsePhoneForInput(p.phone)} x${p.extension ?? ""}`
                  : parsePhoneForInput(p.phone)
              }
              onChange={(ev) => handleCombinedPhoneExtChange(p.id, ev.target.value)}
              placeholder="(555) 555-5555 x"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="col-start-2 row-start-1 text-slate-500 hover:text-red-600"
              onClick={() => removePhone(p.id)}
              aria-label="Remove phone"
            >
              <X className="size-4" />
            </Button>
            <div className="col-start-1 row-start-2 flex min-w-0 justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className={cn("w-[5.5rem] shrink-0 text-xs", stateBtnClass)}
                  >
                    <span className="truncate">
                      {!p.phoneType || p.phoneType === ""
                        ? ""
                        : (PHONE_TYPE_OPTIONS.find((o) => o.value === p.phoneType)?.label ?? "")}
                    </span>
                    <ChevronDown className="size-4 shrink-0 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[8rem]">
                  {PHONE_TYPE_OPTIONS.map((opt) => (
                    <DropdownMenuItem
                      key={opt.value || "empty"}
                      className="flex items-center justify-between gap-2"
                      onClick={() => updatePhone(p.id, p.phone, opt.value, p.extension)}
                    >
                      <span>{opt.label}</span>
                      {opt.value && p.phoneType === opt.value ? (
                        <Check className="size-4 shrink-0" aria-hidden />
                      ) : null}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="col-start-2 row-start-2 size-9 shrink-0" aria-hidden />
          </div>
        ) : (
          <div className="flex flex-nowrap items-center gap-1">
              <Input
                type="tel"
                value={parsePhoneForInput(p.phone)}
                onChange={(ev) => handlePhoneInputChange(p.id, ev.target.value)}
                placeholder="(555) 555-5555"
                className={cn("w-[8.5rem] shrink-0 font-mono", inputClass)}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className={cn("w-[5.5rem] shrink-0 text-xs", stateBtnClass)}
                  >
                    <span className="truncate">
                      {!p.phoneType || p.phoneType === ""
                        ? ""
                        : (PHONE_TYPE_OPTIONS.find((o) => o.value === p.phoneType)?.label ?? "")}
                    </span>
                    <ChevronDown className="size-4 shrink-0 opacity-50" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[8rem]">
                  {PHONE_TYPE_OPTIONS.map((opt) => (
                    <DropdownMenuItem
                      key={opt.value || "empty"}
                      className="flex items-center justify-between gap-2"
                      onClick={() => updatePhone(p.id, p.phone, opt.value)}
                    >
                      <span>{opt.label}</span>
                      {opt.value && p.phoneType === opt.value ? (
                        <Check className="size-4 shrink-0" aria-hidden />
                      ) : null}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="shrink-0 text-slate-500 hover:text-red-600"
                onClick={() => removePhone(p.id)}
                aria-label="Remove phone"
              >
                <X className="size-4" />
              </Button>
          </div>
        )}
    </div>
  )
}

/** Label for picking which phone row gets an extension (dropdown). */
function phoneExtensionTargetLabel(p: PhoneEntry, positionOneBased: number): string {
  const type =
    (p.phoneType && PHONE_TYPE_OPTIONS.find((o) => o.value === p.phoneType)?.label) || ""
  const num = parsePhoneForInput(p.phone).trim()
  const numPart = num || "No number"
  if (type) return `${type} — ${numPart}`
  return `Phone ${positionOneBased} — ${numPart}`
}

type AddressEditBlockProps = {
  a: AddressEntry
  updateAddress: (id: string, field: keyof Omit<AddressEntry, "id">, value: string) => void
  removeAddress: (id: string) => void
  inputClass: string
  stateBtnClass: string
  addressMaxW: string
}

/** Address fields + remove (no drag grip). Shared by list row and floating preview. */
function AddressEditBlock({
  a,
  updateAddress,
  removeAddress,
  inputClass,
  stateBtnClass,
  addressMaxW,
}: AddressEditBlockProps) {
  return (
    <>
      <div className={cn("min-w-0 flex-1 space-y-1.5", addressMaxW)}>
        <Input
          value={a.address ?? ""}
          onChange={(ev) => updateAddress(a.id, "address", ev.target.value)}
          placeholder="Street address"
          className={inputClass}
        />
        <Input
          value={a.address2 ?? ""}
          onChange={(ev) => updateAddress(a.id, "address2", ev.target.value)}
          placeholder="Apartment, suite, etc."
          className={inputClass}
        />
        <div className="space-y-1.5">
          <div className="grid grid-cols-[1fr_5rem] gap-2">
            <Input
              value={a.city ?? ""}
              onChange={(ev) => updateAddress(a.id, "city", ev.target.value)}
              placeholder="City"
              className={inputClass}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={stateBtnClass}
                >
                  <span className="truncate">{a.state ?? ""}</span>
                  <ChevronDown className="size-4 shrink-0 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-[14rem] min-w-[10rem] overflow-y-auto">
                {STATE_OPTIONS.map((opt) => (
                  <DropdownMenuItem
                    key={opt.value || "empty"}
                    onClick={() => updateAddress(a.id, "state", opt.value)}
                  >
                    {opt.value ? `${opt.value}: ${opt.label}` : opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="ml-auto w-1/2 min-w-0">
            <Input
              value={a.zip ?? ""}
              onChange={(ev) => updateAddress(a.id, "zip", ev.target.value)}
              placeholder="ZIP"
              className={inputClass}
            />
          </div>
        </div>
      </div>
      <div className="flex w-7 shrink-0 justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-slate-500 hover:text-red-600"
          onClick={() => removeAddress(a.id)}
          aria-label="Remove address"
        >
          <X className="size-4" />
        </Button>
      </div>
    </>
  )
}

type ContactEditFormProps = {
  firstName: string
  lastName: string
  emails: EmailEntry[]
  phones: PhoneEntry[]
  addresses: AddressEntry[]
  onFirstNameChange: (v: string) => void
  onLastNameChange: (v: string) => void
  onEmailsChange: (v: EmailEntry[]) => void
  onPhonesChange: (v: PhoneEntry[]) => void
  onAddressesChange: (v: AddressEntry[]) => void
  onCancel?: () => void
  onSave?: () => void | Promise<void>
  variant?: ContactEditFormVariant
}

export function ContactEditForm({
  firstName,
  lastName,
  emails,
  phones,
  addresses,
  onFirstNameChange,
  onLastNameChange,
  onEmailsChange,
  onPhonesChange,
  onAddressesChange,
  variant = "page",
}: ContactEditFormProps) {
  const styles = contactEditFormStyles(variant)
  const emailReorder = usePointerListReorder(emails, onEmailsChange)
  const phoneReorder = usePointerListReorder(phones, onPhonesChange)
  const addressReorder = usePointerListReorder(addresses, onAddressesChange)

  const addEmail = useCallback(() => {
    onEmailsChange([...emails, { id: makeId(), email: "" }])
  }, [emails, onEmailsChange])

  const updateEmail = useCallback(
    (id: string, email: string) => {
      onEmailsChange(emails.map((e) => (e.id === id ? { ...e, email } : e)))
    },
    [emails, onEmailsChange]
  )

  const removeEmail = useCallback(
    (id: string) => {
      onEmailsChange(emails.filter((e) => e.id !== id))
    },
    [emails, onEmailsChange]
  )

  const addPhone = useCallback(() => {
    onPhonesChange([...phones, { id: makeId(), phone: "", phoneType: "" }])
  }, [phones, onPhonesChange])

  const updatePhone = useCallback(
    (id: string, phone: string, phoneType: string, extension?: string) => {
      onPhonesChange(
        phones.map((p) =>
          p.id === id ? { ...p, phone, phoneType, ...(extension !== undefined ? { extension } : {}) } : p
        )
      )
    },
    [phones, onPhonesChange]
  )

  const addExtensionToPhone = useCallback(
    (id: string) => {
      const entry = phones.find((p) => p.id === id)
      if (!entry || entry.extension !== undefined) return
      onPhonesChange(
        phones.map((p) => (p.id === id ? { ...p, extension: "" } : p))
      )
    },
    [phones, onPhonesChange]
  )

  const handleCombinedPhoneExtChange = useCallback(
    (id: string, rawValue: string) => {
      const { phone, extension } = parseCombinedPhoneExt(rawValue)
      const entry = phones.find((p) => p.id === id)
      const currentType = entry?.phoneType ?? ""
      onPhonesChange(
        phones.map((p) =>
          p.id === id ? { ...p, phone, extension, phoneType: currentType } : p
        )
      )
    },
    [phones, onPhonesChange]
  )

  const removePhone = useCallback(
    (id: string) => {
      onPhonesChange(phones.filter((p) => p.id !== id))
    },
    [phones, onPhonesChange]
  )

  const handlePhoneInputChange = useCallback(
    (id: string, rawValue: string) => {
      const entry = phones.find((p) => p.id === id)
      const currentType = entry?.phoneType ?? ""
      updatePhone(id, phoneToDigits(rawValue), currentType)
    },
    [phones, updatePhone]
  )

  const addAddress = useCallback(() => {
    onAddressesChange([
      ...addresses,
      {
        id: makeId(),
        address: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
      },
    ])
  }, [addresses, onAddressesChange])

  const updateAddress = useCallback(
    (id: string, field: keyof Omit<AddressEntry, "id">, value: string) => {
      onAddressesChange(
        addresses.map((a) =>
          a.id === id ? { ...a, [field]: value } : a
        )
      )
    },
    [addresses, onAddressesChange]
  )

  const removeAddress = useCallback(
    (id: string) => {
      onAddressesChange(addresses.filter((a) => a.id !== id))
    },
    [addresses, onAddressesChange]
  )

  return (
    <div className={styles.root}>
      <div className={styles.column}>
      {/* First name + Last name on one line */}
      <div className={cn("grid grid-cols-2 gap-2", styles.nameSection)}>
        <div className="space-y-1">
          <Label htmlFor="edit-first-name" className="text-sm font-semibold text-black dark:text-white">
            {variant === "modal" ? "First Name" : "First name"}
          </Label>
          <Input
            id="edit-first-name"
            value={firstName}
            onChange={(e) => onFirstNameChange(e.target.value)}
            placeholder="First name"
            className={styles.input}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="edit-last-name" className="text-sm font-semibold text-black dark:text-white">
            {variant === "modal" ? "Last Name" : "Last name"}
          </Label>
          <Input
            id="edit-last-name"
            value={lastName}
            onChange={(e) => onLastNameChange(e.target.value)}
            placeholder="Last name"
            className={styles.input}
          />
        </div>
      </div>

      {/* Emails */}
      <div className={cn("space-y-1.5", styles.sectionDivider)}>
        <div className="flex items-center justify-start gap-2">
          <Label className="w-20 shrink-0 text-sm font-semibold text-black dark:text-white">
            Email
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={styles.addBtn}
            onClick={addEmail}
          >
            <Plus className="size-3.5 shrink-0" />
            Add
          </Button>
        </div>
        <div
          className={cn(
            "relative flex flex-col",
            emails.length > 1 ? "gap-0" : "gap-0.5"
          )}
        >
          {emailReorder.displayList.map((e) => {
            const isPlaceholder = emailReorder.drag?.id === e.id
            return (
              <div
                key={e.id}
                ref={emailReorder.registerRowRef(e.id)}
                data-email-reorder-row
                className={cn(
                  "rounded-lg border transition-[transform,box-shadow,opacity,border-color] duration-200 ease-out motion-reduce:transition-none",
                  isPlaceholder
                    ? "border-dashed border-sky-400/50 bg-gradient-to-b from-sky-50/90 to-sky-100/40 dark:border-sky-500/50 dark:from-sky-950/50 dark:to-sky-900/30"
                    : "border-transparent",
                  emailReorder.drag &&
                    !isPlaceholder &&
                    "shadow-sm ring-1 ring-slate-200/80 dark:ring-slate-600/80"
                )}
              >
                {isPlaceholder && emailReorder.drag ? (
                  <div
                    className="flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-sky-700/80 dark:text-sky-300/90"
                    style={{ minHeight: emailReorder.drag.height }}
                  >
                    <GripVertical className="size-4 opacity-40" aria-hidden />
                    <span>Drop to reorder</span>
                  </div>
                ) : (
                  <div
                    className={cn(
                      "flex flex-nowrap items-center gap-1 px-0.5",
                      emails.length > 1 ? "py-0" : "py-0.5"
                    )}
                  >
                    <span
                      onPointerDown={(ev) =>
                        emailReorder.onGripPointerDown(ev, e, "[data-email-reorder-row]")
                      }
                      className={cn(
                        "touch-manipulation shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300",
                        emailReorder.drag ? "cursor-grabbing" : "cursor-grab active:cursor-grabbing"
                      )}
                      aria-label="Drag to reorder email"
                    >
                      <GripVertical className="size-4" />
                    </span>
                    <Input
                      type="email"
                      value={e.email}
                      onChange={(ev) => updateEmail(e.id, ev.target.value)}
                      placeholder="email@example.com"
                      className={cn(styles.emailMaxW, "min-w-0 flex-1", styles.input)}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="shrink-0 text-slate-500 hover:text-red-600"
                      onClick={() => removeEmail(e.id)}
                      aria-label="Remove email"
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
        {typeof document !== "undefined" &&
          emailReorder.drag &&
          emailReorder.dragFloater &&
          createPortal(
            <div
              className="pointer-events-none fixed z-[300] rounded-xl border border-sky-200/90 bg-background/95 p-2 shadow-2xl shadow-sky-900/15 ring-2 ring-sky-400/25 backdrop-blur-sm dark:border-sky-700/80 dark:bg-card/95 dark:ring-sky-500/30"
              style={{
                left: emailReorder.drag.x,
                top: emailReorder.drag.y,
                width: emailReorder.drag.width,
                boxShadow:
                  "0 25px 50px -12px rgb(0 0 0 / 0.25), 0 0 0 1px rgb(14 165 233 / 0.12), 0 12px 24px -8px rgb(14 165 233 / 0.2)",
              }}
            >
              <div className="flex flex-nowrap items-center gap-1">
                <span className="shrink-0 rounded-md p-1 text-slate-300 dark:text-slate-600">
                  <GripVertical className="size-4" />
                </span>
                <Input
                  type="email"
                  readOnly
                  tabIndex={-1}
                  value={emailReorder.dragFloater.email}
                  className={cn(styles.emailMaxW, "min-w-0 flex-1 cursor-inherit bg-transparent", styles.input)}
                />
                <span className="size-9 shrink-0" aria-hidden />
              </div>
            </div>,
            document.body
          )}
      </div>

      {/* Phones: number with (234) 234-2345 formatting + type popup + optional extension */}
      <div className={cn("space-y-1.5", styles.sectionDivider)}>
        <div className="flex items-center justify-start gap-2">
          <Label className="w-20 shrink-0 text-sm font-semibold text-black dark:text-white">
            Phone
          </Label>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={styles.addBtn}
              onClick={addPhone}
            >
              <Plus className="size-3.5 shrink-0" />
              Add
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={
                    phones.filter((p) => p.extension === undefined).length === 0
                  }
                  title={
                    phones.filter((p) => p.extension === undefined).length === 0
                      ? "All phone rows already have an extension"
                      : "Add extension to a phone number"
                  }
                  className={styles.addBtn}
                >
                  <Plus className="size-3.5 shrink-0" />
                  Extension
                  <ChevronDown className="size-3.5 shrink-0 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                className="min-w-[12rem] max-w-[min(20rem,calc(100vw-2rem))]"
              >
                {phones
                  .filter((p) => p.extension === undefined)
                  .map((p) => {
                    const positionOneBased = phones.findIndex((x) => x.id === p.id) + 1
                    return (
                      <DropdownMenuItem
                        key={p.id}
                        onClick={() => addExtensionToPhone(p.id)}
                      >
                        {phoneExtensionTargetLabel(p, positionOneBased)}
                      </DropdownMenuItem>
                    )
                  })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        <div
          className={cn(
            "relative flex flex-col",
            phones.length > 1 ? "gap-0" : "gap-0.5"
          )}
        >
          {phoneReorder.displayList.map((p) => {
            const isPlaceholder = phoneReorder.drag?.id === p.id
            return (
              <div
                key={p.id}
                ref={phoneReorder.registerRowRef(p.id)}
                data-phone-reorder-row
                className={cn(
                  "rounded-lg border transition-[transform,box-shadow,opacity,border-color] duration-200 ease-out motion-reduce:transition-none",
                  isPlaceholder
                    ? "border-dashed border-sky-400/50 bg-gradient-to-b from-sky-50/90 to-sky-100/40 dark:border-sky-500/50 dark:from-sky-950/50 dark:to-sky-900/30"
                    : "border-transparent",
                  phoneReorder.drag &&
                    !isPlaceholder &&
                    "shadow-sm ring-1 ring-slate-200/80 dark:ring-slate-600/80"
                )}
              >
                {isPlaceholder && phoneReorder.drag ? (
                  <div
                    className="flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-sky-700/80 dark:text-sky-300/90"
                    style={{ minHeight: phoneReorder.drag.height }}
                  >
                    <GripVertical className="size-4 opacity-40" aria-hidden />
                    <span>Drop to reorder</span>
                  </div>
                ) : (
                  <div
                    className={cn(
                      "flex flex-nowrap items-center gap-1 px-0.5",
                      phones.length > 1 ? "py-0" : "py-0.5"
                    )}
                  >
                    <span
                      onPointerDown={(ev) =>
                        phoneReorder.onGripPointerDown(ev, p, "[data-phone-reorder-row]")
                      }
                      className={cn(
                        "touch-manipulation shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300",
                        phoneReorder.drag ? "cursor-grabbing" : "cursor-grab active:cursor-grabbing"
                      )}
                      aria-label="Drag to reorder phone"
                    >
                      <GripVertical className="size-4" />
                    </span>
                    <PhoneRowContent
                      p={p}
                      updatePhone={updatePhone}
                      removePhone={removePhone}
                      handleCombinedPhoneExtChange={handleCombinedPhoneExtChange}
                      handlePhoneInputChange={handlePhoneInputChange}
                      inputClass={styles.input}
                      stateBtnClass={styles.stateBtn}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
        {typeof document !== "undefined" &&
          phoneReorder.drag &&
          phoneReorder.dragFloater &&
          createPortal(
            <div
              className="pointer-events-none fixed z-[300] rounded-xl border border-sky-200/90 bg-background/95 p-2 shadow-2xl shadow-sky-900/15 ring-2 ring-sky-400/25 backdrop-blur-sm dark:border-sky-700/80 dark:bg-card/95 dark:ring-sky-500/30"
              style={{
                left: phoneReorder.drag.x,
                top: phoneReorder.drag.y,
                width: phoneReorder.drag.width,
                boxShadow:
                  "0 25px 50px -12px rgb(0 0 0 / 0.25), 0 0 0 1px rgb(14 165 233 / 0.12), 0 12px 24px -8px rgb(14 165 233 / 0.2)",
              }}
            >
              <div className="flex flex-nowrap items-center gap-1">
                <span className="shrink-0 rounded-md p-1 text-slate-300 dark:text-slate-600">
                  <GripVertical className="size-4" />
                </span>
                <PhoneRowContent
                  p={phoneReorder.dragFloater}
                  updatePhone={updatePhone}
                  removePhone={removePhone}
                  handleCombinedPhoneExtChange={handleCombinedPhoneExtChange}
                  handlePhoneInputChange={handlePhoneInputChange}
                  inputClass={styles.input}
                  stateBtnClass={styles.stateBtn}
                />
              </div>
            </div>,
            document.body
          )}
      </div>
      </div>

      {/* Addresses */}
      <div className={styles.columnDivider}>
      <div className="space-y-1.5">
        <div className="flex items-center justify-start gap-2">
          <Label className="w-20 shrink-0 text-sm font-semibold text-black dark:text-white">
            Address
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={styles.addBtn}
            onClick={addAddress}
          >
            <Plus className="size-3.5 shrink-0" />
            Add
          </Button>
        </div>
        <div
          className={cn(
            "relative flex flex-col",
            addresses.length > 1 ? "gap-0" : "gap-0.5"
          )}
        >
          {addressReorder.displayList.map((a) => {
            const isPlaceholder = addressReorder.drag?.id === a.id
            return (
              <div
                key={a.id}
                ref={addressReorder.registerRowRef(a.id)}
                data-address-reorder-row
                className={cn(
                  "rounded-lg border transition-[transform,box-shadow,opacity,border-color] duration-200 ease-out motion-reduce:transition-none",
                  isPlaceholder
                    ? "border-dashed border-sky-400/50 bg-gradient-to-b from-sky-50/90 to-sky-100/40 dark:border-sky-500/50 dark:from-sky-950/50 dark:to-sky-900/30"
                    : "border-transparent",
                  addressReorder.drag &&
                    !isPlaceholder &&
                    "shadow-sm ring-1 ring-slate-200/80 dark:ring-slate-600/80"
                )}
              >
                {isPlaceholder && addressReorder.drag ? (
                  <div
                    className="flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium text-sky-700/80 dark:text-sky-300/90"
                    style={{ minHeight: addressReorder.drag.height }}
                  >
                    <GripVertical className="size-4 opacity-40" aria-hidden />
                    <span>Drop to reorder</span>
                  </div>
                ) : (
                  <div
                    className={cn(
                      "flex items-start gap-1 px-0.5",
                      addresses.length > 1 ? "py-0" : "py-0.5"
                    )}
                  >
                    <span
                      onPointerDown={(ev) =>
                        addressReorder.onGripPointerDown(ev, a, "[data-address-reorder-row]")
                      }
                      className={cn(
                        "touch-manipulation shrink-0 rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-300",
                        addressReorder.drag ? "cursor-grabbing" : "cursor-grab active:cursor-grabbing"
                      )}
                      aria-label="Drag to reorder address"
                    >
                      <GripVertical className="size-4" />
                    </span>
                    <AddressEditBlock
                      a={a}
                      updateAddress={updateAddress}
                      removeAddress={removeAddress}
                      inputClass={styles.input}
                      stateBtnClass={styles.stateBtn}
                      addressMaxW={styles.addressMaxW}
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
        {typeof document !== "undefined" &&
          addressReorder.drag &&
          addressReorder.dragFloater &&
          createPortal(
            <div
              className="pointer-events-none fixed z-[300] rounded-xl border border-sky-200/90 bg-background/95 p-2 shadow-2xl shadow-sky-900/15 ring-2 ring-sky-400/25 backdrop-blur-sm dark:border-sky-700/80 dark:bg-card/95 dark:ring-sky-500/30"
              style={{
                left: addressReorder.drag.x,
                top: addressReorder.drag.y,
                width: addressReorder.drag.width,
                boxShadow:
                  "0 25px 50px -12px rgb(0 0 0 / 0.25), 0 0 0 1px rgb(14 165 233 / 0.12), 0 12px 24px -8px rgb(14 165 233 / 0.2)",
              }}
            >
              <div className="flex items-start gap-1">
                <span className="shrink-0 rounded-md p-1 text-slate-300 dark:text-slate-600">
                  <GripVertical className="size-4" />
                </span>
                <AddressEditBlock
                  a={addressReorder.dragFloater}
                  updateAddress={updateAddress}
                  removeAddress={removeAddress}
                  inputClass={styles.input}
                  stateBtnClass={styles.stateBtn}
                  addressMaxW={styles.addressMaxW}
                />
              </div>
            </div>,
            document.body
          )}
      </div>
      </div>

    </div>
  )
}
